import React from 'react';
import { Building } from 'lucide-react';
import { PageHeader, PageHero, PageFooter } from '../components/PageLayout';
import useSEO from '../hooks/useSEO';

const LegalPage = () => {
  useSEO({
    title: 'Mentions Légales | DJ Nomad',
    description: 'Mentions légales de DJ Nomad. Informations sur l\'éditeur et l\'hébergeur.',
    canonical: 'https://djnomad.app/legal',
    robots: 'noindex, follow'
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <PageHeader />

      {/* Hero */}
      <PageHero 
        icon={Building}
        title="Mentions Légales"
        subtitle="Informations légales et responsabilités"
      />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 flex-1">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10 shadow-sm">
          <div className="prose max-w-none space-y-8 text-gray-600">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Éditeur de l'Application</h2>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 space-y-3">
                <p><strong className="text-gray-900">Nom :</strong> DJ Nomad</p>
                <p><strong className="text-gray-900">Forme juridique :</strong> Entreprise individuelle / Micro-entreprise</p>
                <p><strong className="text-gray-900">Siège social :</strong> Paris, France</p>
                <p><strong className="text-gray-900">Email :</strong> <a href="mailto:contact@djnomad.app" className="text-violet-600 hover:underline">contact@djnomad.app</a></p>
                <p><strong className="text-gray-900">Directeur de publication :</strong> Équipe DJ Nomad</p>
              </div>
              <p className="text-sm italic mt-4">
                Note : DJ Nomad est un projet en cours de structuration juridique. Les informations complètes seront mises à jour dès la création officielle de la société.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Hébergement</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">2.1 Application web</h3>
              <div className="space-y-2">
                <p><strong className="text-gray-900">Hébergeur :</strong> Emergent Cloud Services</p>
                <p><strong className="text-gray-900">Localisation :</strong> Europe (conformité RGPD)</p>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">2.2 Base de données</h3>
              <div className="space-y-2">
                <p><strong className="text-gray-900">Service :</strong> MongoDB Atlas</p>
                <p><strong className="text-gray-900">Localisation :</strong> Union Européenne</p>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">2.3 Stockage fichiers (optionnel)</h3>
              <div className="space-y-2">
                <p><strong className="text-gray-900">Service :</strong> Cloudflare R2</p>
                <p><strong className="text-gray-900">Localisation :</strong> Réseau mondial avec priorité Europe</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Propriété Intellectuelle</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">3.1 Contenu de l'Application</h3>
              <p className="leading-relaxed">
                L'ensemble du contenu de l'Application DJ Nomad (structure, graphismes, textes, logos, icônes, sons, logiciels, etc.) est la propriété exclusive de DJ Nomad, sauf mention contraire.
              </p>
              <p className="leading-relaxed">
                Toute reproduction, représentation, modification, publication, adaptation totale ou partielle du contenu de l'Application, par quelque procédé que ce soit, est interdite sans autorisation écrite préalable de DJ Nomad.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">3.2 Marques et logos</h3>
              <p className="leading-relaxed">
                "DJ Nomad" ainsi que les logos associés sont des marques déposées ou en cours de dépôt. Toute utilisation sans autorisation constitue une contrefaçon passible de sanctions.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">3.3 Contenu utilisateur</h3>
              <p className="leading-relaxed">
                Les fichiers musicaux et contenus que vous uploadez restent votre propriété. Vous accordez à DJ Nomad une licence limitée pour héberger et afficher ce contenu dans le cadre du service, sans céder vos droits de propriété.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Responsabilités</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">4.1 Responsabilité de l'éditeur</h3>
              <p className="leading-relaxed">
                DJ Nomad met tout en œuvre pour assurer le bon fonctionnement de l'Application, mais ne peut garantir :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Une disponibilité permanente et sans interruption</li>
                <li>L'absence totale d'erreurs ou de bugs</li>
                <li>La compatibilité avec tous les navigateurs et appareils</li>
                <li>La sécurité absolue contre toute intrusion ou piratage</li>
              </ul>
              <p className="leading-relaxed mt-4">
                DJ Nomad décline toute responsabilité en cas de :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Perte de données due à un dysfonctionnement technique</li>
                <li>Dommages causés par un virus informatique</li>
                <li>Utilisation abusive ou frauduleuse de l'Application par un tiers</li>
                <li>Interruption d'événement due à des problèmes réseau ou matériels</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">4.2 Responsabilité de l'utilisateur</h3>
              <p className="leading-relaxed">
                L'utilisateur est seul responsable :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Du respect des droits d'auteur sur les fichiers qu'il uploade</li>
                <li>De la sécurité de ses identifiants de connexion</li>
                <li>De l'utilisation conforme et légale de l'Application</li>
                <li>Des dommages causés par une utilisation frauduleuse</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Liens Hypertextes</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">5.1 Liens sortants</h3>
              <p className="leading-relaxed">
                L'Application peut contenir des liens vers des sites tiers. DJ Nomad n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu, leurs pratiques de confidentialité ou leur disponibilité.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">5.2 Liens entrants</h3>
              <p className="leading-relaxed">
                La création de liens vers l'Application est autorisée, sous réserve :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>De ne pas présenter l'Application dans un cadre (frame)</li>
                <li>De ne pas induire en erreur sur la provenance du contenu</li>
                <li>De ne pas porter atteinte à l'image de DJ Nomad</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Protection des Données Personnelles</h2>
              <p className="leading-relaxed">
                Le traitement de vos données personnelles est décrit en détail dans notre <a href="/privacy" className="text-violet-600 hover:underline font-semibold">Politique de Confidentialité</a>.
              </p>
              <p className="leading-relaxed">
                Conformément au RGPD et à la loi Informatique et Libertés, vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, de portabilité et d'opposition sur vos données.
              </p>
              <p className="leading-relaxed">
                Contact DPO (Data Protection Officer) : <a href="mailto:privacy@djnomad.app" className="text-violet-600 hover:underline">privacy@djnomad.app</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies</h2>
              <p className="leading-relaxed">
                L'Application utilise des cookies pour améliorer l'expérience utilisateur et assurer son bon fonctionnement. Les détails sur l'utilisation des cookies sont disponibles dans notre <a href="/privacy" className="text-violet-600 hover:underline font-semibold">Politique de Confidentialité</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Loi Applicable et Juridiction</h2>
              <p className="leading-relaxed">
                Les présentes mentions légales sont régies par le droit français. Tout litige relatif à l'utilisation de l'Application sera soumis à la compétence exclusive des tribunaux de Paris, France.
              </p>
              <p className="leading-relaxed">
                Conformément aux articles L. 616-1 et R. 616-1 du Code de la consommation, nous proposons un dispositif de médiation de la consommation. L'entité de médiation retenue est :
              </p>
              <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 mt-3">
                <p className="font-semibold text-gray-900">Médiateur de la consommation</p>
                <p className="text-sm mt-2">Site : <a href="https://www.economie.gouv.fr/mediation-conso" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">www.economie.gouv.fr/mediation-conso</a></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Crédits</h2>
              <p className="leading-relaxed">Conception et développement : Équipe DJ Nomad</p>
              <p className="leading-relaxed">Icônes : Lucide Icons (MIT License)</p>
              <p className="leading-relaxed">Technologies : React, FastAPI, MongoDB, PWA</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Modifications</h2>
              <p className="leading-relaxed">
                DJ Nomad se réserve le droit de modifier les présentes mentions légales à tout moment. Les modifications entrent en vigueur dès leur publication sur l'Application.
              </p>
              <p className="leading-relaxed">
                Date de dernière modification : <strong className="text-gray-900">17 décembre 2024</strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact</h2>
              <p className="leading-relaxed">
                Pour toute question concernant les présentes mentions légales, vous pouvez nous contacter :
              </p>
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-6 mt-4 space-y-3">
                <p><strong className="text-gray-900">Email :</strong> <a href="mailto:contact@djnomad.app" className="text-violet-600 hover:underline">contact@djnomad.app</a></p>
                <p><strong className="text-gray-900">Email légal :</strong> <a href="mailto:legal@djnomad.app" className="text-violet-600 hover:underline">legal@djnomad.app</a></p>
                <p><strong className="text-gray-900">Formulaire :</strong> <a href="/contact" className="text-violet-600 hover:underline">djnomad.app/contact</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default LegalPage;